import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { talentAPI } from '../../utils/api';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Alert, AlertDescription, AlertTitle } from '../../components/ui/alert';
import { Badge } from '../../components/ui/badge';
import { 
  LogOut, Calendar, DollarSign, Award, Shield, AlertTriangle, 
  CheckCircle, Clock, XCircle, FileText, Camera, Star, Upload, Bell, MessageSquare, ClipboardCheck, Crown, Zap, Image, Clipboard, Briefcase
} from 'lucide-react';
import ProfileCompleteness from '../../components/ProfileCompleteness';
import ProfileQualityCard from '../../components/ProfileQualityCard';
import PerformanceScoreCard from '../../components/PerformanceScoreCard';
import BehavioralMetricsCard from '../../components/BehavioralMetricsCard';
import TalentGrowthTips from '../../components/TalentGrowthTips';
import ShiftConfirmationWidget from '../../components/talent/ShiftConfirmationWidget';
import SurveyPayoutBanner from '../../components/talent/SurveyPayoutBanner';
import BackupAckBanner from '../../components/talent/BackupAckBanner';
import PromotionResponseBanner from '../../components/talent/PromotionResponseBanner';
import StreakReminderBanner from '../../components/talent/StreakReminderBanner';
import WorkersCompUploadModal from '../../components/WorkersCompUploadModal';
import RejectionBanner from '../../components/RejectionBanner';
import MyAgenciesWidget from '../../components/MyAgenciesWidget';

const TalentDashboard = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [profile, setProfile] = useState(null);
  const [idvStatus, setIdvStatus] = useState(null);
  const [settlementSummary, setSettlementSummary] = useState(null);
  const [ratingsData, setRatingsData] = useState(null);
  const [contractCompliance, setContractCompliance] = useState({ is_compliant: true });
  const [unreadNotifications, setUnreadNotifications] = useState(0);
  const [stats, setStats] = useState({
    upcomingGigs: 0,
  });
  const [loading, setLoading] = useState(true);
  const [workersCompOpen, setWorkersCompOpen] = useState(false);
  // Gentle headshot nudge — dismissible per session so it reminds without nagging.
  const [headshotNudgeDismissed, setHeadshotNudgeDismissed] = useState(() => {
    try { return sessionStorage.getItem('headshot_nudge_dismissed') === '1'; } catch { return false; }
  });
  const dismissHeadshotNudge = () => {
    try { sessionStorage.setItem('headshot_nudge_dismissed', '1'); } catch { /* noop */ }
    setHeadshotNudgeDismissed(true);
  };

  useEffect(() => {
    // Audit-2026-08-27 P0 — Proactive ICMA reaccept gate.
    // The Axios interceptor only fires on 403 from gated actions
    // (/apply, /accept-invite, /confirm). Talent who just log in and
    // browse the marketplace never trigger it, so they never see the
    // revised agreement. Check /api/icma/my-status on Dashboard mount
    // and force-redirect to /talent/agreement if the current user
    // has not accepted the active agreement. This does NOT block
    // grandfathered access to existing bookings — those pages route
    // directly and the check only fires on Dashboard mount.
    const checkAgreementGate = async () => {
      try {
        const token = localStorage.getItem('token');
        const baseUrl = process.env.REACT_APP_BACKEND_URL;
        const raw = await fetch(`${baseUrl}/api/icma/my-status`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (raw.ok) {
          const d = await raw.json();
          // Skip redirect while ICMA enforcement is suspended — the
          // active agreement is being replaced; existing accepted
          // contractors remain under their prior valid terms.
          if (d?.enforcement_suspended === true) {
            return false;
          }
          if (d?.requires_agreement_reaccept === true) {
            navigate('/talent/agreement', { replace: true });
            return true;
          }
        }
      } catch {
        // Best-effort — never block dashboard load on this check.
      }
      return false;
    };
    (async () => {
      const gated = await checkAgreementGate();
      if (!gated) fetchData();
    })();
  }, []);

  const fetchData = async () => {
    const token = localStorage.getItem('token');
    const headers = { Authorization: `Bearer ${token}` };
    const baseUrl = process.env.REACT_APP_BACKEND_URL;
    
    try {
      const [profileRes, bookingsRes] = await Promise.all([
        talentAPI.getProfile(),
        talentAPI.getBookings(),
      ]);

      setProfile(profileRes.data);
      setStats({
        upcomingGigs: bookingsRes.data.filter(
          (b) => new Date(b.gig_date || b.shift_date) >= new Date() && b.status === 'confirmed'
        ).length,
      });

      // Fetch IDV status
      try {
        const idvRes = await fetch(`${baseUrl}/api/idv/status`, { headers });
        const idv = await idvRes.json();
        setIdvStatus(idv);
      } catch (e) {
        console.error('Error fetching IDV status:', e);
      }

      // Fetch settlement summary
      try {
        const settlementRes = await fetch(`${baseUrl}/api/talent/settlements/summary`, { headers });
        const settlement = await settlementRes.json();
        setSettlementSummary(settlement);
      } catch (e) {
        console.error('Error fetching settlement summary:', e);
      }

      // Fetch ratings
      try {
        const ratingsRes = await fetch(`${baseUrl}/api/talent/ratings`, { headers });
        const ratings = await ratingsRes.json();
        setRatingsData(ratings);
      } catch (e) {
        console.error('Error fetching ratings:', e);
      }

      // Check contract compliance
      try {
        const complianceRes = await fetch(`${baseUrl}/api/contracts/check-compliance`, { headers });
        const compliance = await complianceRes.json();
        setContractCompliance(compliance);
      } catch (e) {
        console.error('Error checking compliance:', e);
      }

      // Fetch notification count
      try {
        const notifRes = await fetch(`${baseUrl}/api/notifications/talent?unread_only=true`, { headers });
        const notifData = await notifRes.json();
        setUnreadNotifications(notifData.unread_count || 0);
      } catch (e) {
        console.error('Error fetching notifications:', e);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const getIdvStatusIcon = () => {
    switch (idvStatus?.idv_status) {
      case 'approved':
        return <CheckCircle className="h-5 w-5 text-green-600" />;
      case 'pending_review':
        return <Clock className="h-5 w-5 text-amber-600" />;
      case 'rejected':
        return <XCircle className="h-5 w-5 text-red-600" />;
      default:
        return <Shield className="h-5 w-5 text-gray-400" />;
    }
  };

  const getIdvStatusMessage = () => {
    switch (idvStatus?.idv_status) {
      case 'approved':
        return { title: 'Verified', desc: 'Your identity is verified. You can be booked for gigs.', variant: 'success' };
      case 'pending_review':
        return { title: 'Verification Pending', desc: 'Your ID is under admin review. You\'ll be notified once reviewed.', variant: 'warning' };
      case 'rejected':
        return { 
          title: 'Verification Declined', 
          desc: idvStatus.retry_message || 'Your verification was declined. Please upload new documents.',
          variant: 'error' 
        };
      default:
        return { 
          title: 'Identity Verification Required', 
          desc: 'Upload your Driver\'s License (front & back) to be eligible for bookings and settlements.',
          variant: 'default' 
        };
    }
  };

  const getPremiumStatusBadge = () => {
    if (!profile) return null;
    
    switch (profile.premium_profile_status) {
      case 'approved':
        return (
          <Badge className="bg-purple-100 text-purple-800 border border-purple-200">
            <Star className="w-3 h-3 mr-1" /> Premium
          </Badge>
        );
      case 'pending':
        return (
          <Badge className="bg-amber-100 text-amber-800 border border-amber-200">
            <Clock className="w-3 h-3 mr-1" /> Premium Pending
          </Badge>
        );
      case 'rejected':
        return (
          <Badge className="bg-red-100 text-red-800 border border-red-200">
            <XCircle className="w-3 h-3 mr-1" /> Premium Rejected
          </Badge>
        );
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-500"></div>
      </div>
    );
  }

  const idvMessage = getIdvStatusMessage();

  return (
    <div className="min-h-screen bg-gray-50 overflow-x-hidden" data-testid="talent-dashboard">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-3 sm:py-4 flex justify-between items-center gap-2">
          <h1 className="text-xl sm:text-2xl font-bold text-cyan-600 shrink-0">iEvntür</h1>
          <div className="flex items-center gap-1.5 sm:gap-3 shrink-0">
            {/* Notification Bell */}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/talent/notifications')}
              className="relative text-gray-600 hover:text-gray-900 px-2"
              data-testid="notifications-button"
              title="Notifications"
            >
              <Bell className="w-5 h-5" />
              {unreadNotifications > 0 && (
                <span className="absolute -top-1 -right-1 bg-cyan-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                  {unreadNotifications > 9 ? '9+' : unreadNotifications}
                </span>
              )}
            </Button>
            <span className="text-sm text-gray-600 hidden lg:inline truncate max-w-[200px]">{user?.email}</span>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={handleLogout} 
              className="border-gray-300 text-gray-700 hover:bg-gray-100 px-2 sm:px-3"
              data-testid="logout-button"
              title="Logout"
            >
              <LogOut className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Logout</span>
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Iter-205 — Rejection alerts (IDV / certs) with re-upload CTA */}
        <RejectionBanner />
        {/* Share-streak nudge — gentle in-app reminder when streak is at risk */}
        <StreakReminderBanner />
        {/* IDV Status Banner */}
        {idvStatus && (
          <Alert 
            className={`mb-6 ${
              idvMessage.variant === 'success' 
                ? 'bg-green-50 border-green-300' 
                : idvMessage.variant === 'error'
                ? 'bg-red-50 border-red-300'
                : idvMessage.variant === 'warning'
                ? 'bg-amber-50 border-amber-300'
                : 'bg-gray-50 border-gray-300'
            }`}
            data-testid="idv-status-banner"
          >
            <div className="flex items-start gap-3">
              {getIdvStatusIcon()}
              <div className="flex-1">
                <AlertTitle className={`font-semibold ${
                  idvMessage.variant === 'success' ? 'text-green-800' :
                  idvMessage.variant === 'error' ? 'text-red-800' :
                  idvMessage.variant === 'warning' ? 'text-amber-800' :
                  'text-gray-800'
                }`}>
                  {idvMessage.title}
                </AlertTitle>
                <AlertDescription className="text-gray-600 mt-1">
                  {idvMessage.desc}
                </AlertDescription>
                {(idvStatus.idv_status === 'not_started' || idvStatus.can_retry) && (
                  <Button 
                    size="sm" 
                    className="mt-3 bg-cyan-500 hover:bg-cyan-600 text-white"
                    onClick={() => navigate('/talent/verify')}
                    data-testid="verify-now-btn"
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    {idvStatus.idv_status === 'rejected' ? 'Upload New Documents' : 'Upload ID Documents'}
                  </Button>
                )}
              </div>
            </div>
          </Alert>
        )}

        {/* Contract Compliance Warning */}
        {!contractCompliance.is_compliant && (
          <Alert className="mb-6 bg-amber-50 border-amber-300">
            <FileText className="h-4 w-4 text-amber-600" />
            <AlertDescription className="text-amber-800">
              You have {contractCompliance.pending_required_contracts} required contract(s) to accept.
              <Button 
                variant="link" 
                className="text-cyan-600 p-0 ml-2 font-medium"
                onClick={() => navigate('/talent/contracts')}
              >
                View Contracts →
              </Button>
            </AlertDescription>
          </Alert>
        )}

        {/* Profile Photo — gentle headshot reminder */}
        {profile && !profile.profile_photo_url && (
          <Alert className="mb-6 bg-cyan-50 border-cyan-200" data-testid="headshot-missing-banner">
            <Camera className="h-4 w-4 text-cyan-600" />
            <AlertDescription className="text-cyan-900">
              Add a professional headshot to help clients recognise you and book you faster — a clear,
              front-facing photo (no sunglasses or heavy filters) works best.
              <Button 
                variant="link" 
                className="text-cyan-600 p-0 ml-2 font-medium"
                onClick={() => navigate('/talent/profile')}
                data-testid="headshot-missing-cta"
              >
                Upload Headshot
              </Button>
            </AlertDescription>
          </Alert>
        )}

        {/* Gentle nudge for talent who already have a photo — keep it current */}
        {profile && profile.profile_photo_url && !headshotNudgeDismissed && (
          <Alert className="mb-6 bg-gray-50 border-gray-200" data-testid="headshot-refresh-banner">
            <Camera className="h-4 w-4 text-cyan-600" />
            <div className="flex items-start justify-between gap-3 w-full">
              <AlertDescription className="text-gray-700">
                Is your headshot still current? A fresh, professional photo keeps your booking card
                looking its best.
                <Button
                  variant="link"
                  className="text-cyan-600 p-0 ml-2 font-medium"
                  onClick={() => navigate('/talent/profile')}
                  data-testid="headshot-refresh-cta"
                >
                  Update Photo
                </Button>
              </AlertDescription>
              <button
                onClick={dismissHeadshotNudge}
                className="text-gray-400 hover:text-gray-600 shrink-0"
                aria-label="Dismiss"
                data-testid="headshot-refresh-dismiss"
              >
                <XCircle className="w-4 h-4" />
              </button>
            </div>
          </Alert>
        )}

        {/* Iter-168 Phase 3 — Profile Quality (weighted 0–100) */}
        <ProfileQualityCard />

        {/* Overall standing — moved from Home so talent see performance in
            one place (their profile hub) instead of cluttering Home. */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-2">
          <PerformanceScoreCard />
          <BehavioralMetricsCard />
        </div>

        {/* Profile Completeness Indicator */}
        <ProfileCompleteness />

        {/* Shift Confirmation Widget - Shows pending confirmations */}
        <ShiftConfirmationWidget />

        {/* Iter-133u (Phase D) — Post-event survey blocking payout banner */}
        <SurveyPayoutBanner />

        {/* Iter-133x (P0) — Backup role acknowledgment banner */}
        <BackupAckBanner />
        <PromotionResponseBanner />

        {/* Iter-228 — My Agencies widget */}
        <MyAgenciesWidget />

        {/* Profile Card */}
        {profile && (
          <Card className="mb-8 bg-white border-gray-200 shadow-sm" data-testid="profile-card">
            <CardContent className="pt-6">
              <div className="flex justify-between items-start">
                <div className="flex items-center gap-4">
                  {profile.profile_photo_url ? (
                    <img 
                      src={profile.profile_photo_url} 
                      alt="Profile" 
                      className="w-16 h-16 rounded-full object-cover border-2 border-cyan-500"
                    />
                  ) : (
                    <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center border-2 border-gray-200">
                      <Camera className="w-6 h-6 text-gray-400" />
                    </div>
                  )}
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">
                      {profile.first_name} {profile.last_name}
                    </h2>
                    <p className="text-gray-500">
                      {profile.city}, {profile.state}
                    </p>
                    <div className="mt-2 flex items-center gap-3 flex-wrap">
                      <Badge className={`${
                        profile.tier === 'premium' 
                          ? 'bg-purple-100 text-purple-800 border border-purple-200' 
                          : 'bg-cyan-100 text-cyan-800 border border-cyan-200'
                      }`}>
                        {profile.tier === 'premium' ? 'Premium' : 'Flex'} Tier
                      </Badge>
                      <div className="flex items-center gap-1">
                        <Award className="w-4 h-4 text-amber-500" />
                        <span className="text-sm font-medium text-gray-700">{profile.reliability_score}%</span>
                      </div>
                      {ratingsData && ratingsData.average_rating > 0 && (
                        <div className="flex items-center gap-1">
                          <Star className="w-4 h-4 text-amber-500 fill-amber-500" />
                          <span className="text-sm font-medium text-gray-700">
                            {ratingsData.average_rating.toFixed(1)} ({ratingsData.total_ratings})
                          </span>
                        </div>
                      )}
                      {idvStatus?.idv_status === 'approved' && (
                        <span className="inline-flex items-center gap-1 text-sm text-green-600 font-medium">
                          <CheckCircle className="w-4 h-4" /> Verified
                        </span>
                      )}
                      {getPremiumStatusBadge()}
                    </div>
                    {/* Premium Eligibility Notice */}
                    {profile.is_premium_eligible && profile.premium_profile_status === 'none' && (
                      <p className="text-sm text-purple-600 mt-2">
                        <Star className="w-3 h-3 inline mr-1" />
                        Eligible for Premium status. 
                        <Button 
                          variant="link" 
                          className="text-purple-600 p-0 ml-1 font-medium"
                          onClick={() => navigate('/talent/premium-apply')}
                        >
                          Apply now →
                        </Button>
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          <Card className="bg-white border-gray-200 shadow-sm" data-testid="upcoming-gigs-card">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-gray-600">Upcoming Gigs</CardTitle>
              <Calendar className="h-4 w-4 text-cyan-500" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-gray-900">{stats.upcomingGigs}</div>
            </CardContent>
          </Card>

          <Card className="bg-white border-gray-200 shadow-sm md:col-span-2" data-testid="settlement-lifecycle-card">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-gray-600">My Settlements</CardTitle>
              <DollarSign className="h-4 w-4 text-cyan-500" />
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {[
                  { key: 'pending_approval', label: 'Pending Approval', amount: settlementSummary?.pending_approval_amount, color: 'text-amber-600' },
                  { key: 'approved', label: 'Approved / Upcoming', amount: settlementSummary?.approved_upcoming_amount, color: 'text-blue-600' },
                  { key: 'payment_processing', label: 'Processing', amount: settlementSummary?.processing_amount, color: 'text-indigo-600' },
                  { key: 'paid', label: 'Paid', amount: settlementSummary?.paid_amount, color: 'text-green-600' },
                ].map((t) => (
                  <button
                    key={t.key}
                    type="button"
                    onClick={() => navigate('/talent/settlements')}
                    data-testid={`dashboard-settlement-${t.key}`}
                    className="text-left rounded-lg border border-gray-100 hover:border-cyan-300 hover:bg-cyan-50/40 transition px-3 py-2"
                  >
                    <p className="text-[11px] text-gray-500 leading-tight">{t.label}</p>
                    <p className={`text-lg font-bold ${t.color}`}>${Number(t.amount || 0).toFixed(2)}</p>
                  </button>
                ))}
              </div>
              {settlementSummary?.under_review_amount > 0 && (
                <p className="text-xs text-purple-600 mt-3">
                  ${Number(settlementSummary.under_review_amount).toFixed(2)} under review (challenge in progress)
                </p>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Workers Comp Notice */}
        {settlementSummary && !settlementSummary.has_workers_comp && (
          <Alert className="mb-6 bg-gray-50 border-gray-300">
            <AlertTriangle className="h-4 w-4 text-gray-500" />
            <AlertDescription className="text-gray-600">
              <span className="font-medium">Platform Adjustment Notice:</span> A ${settlementSummary.oa_rate_per_hour}/hr 
              OA Insurance adjustment applies because you don't have verified workers compensation on file.
              <Button 
                variant="link" 
                className="text-cyan-600 p-0 ml-2 font-medium"
                onClick={() => setWorkersCompOpen(true)}
                data-testid="open-workers-comp-modal-btn"
              >
                Upload Workers Comp →
              </Button>
            </AlertDescription>
          </Alert>
        )}

        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Button
            onClick={() => navigate('/talent/gigs')}
            className="h-20 text-lg bg-cyan-500 hover:bg-cyan-600 text-white font-semibold shadow-sm"
            data-testid="browse-gigs-button"
          >
            <Calendar className="w-5 h-5 mr-2" />
            Browse Available Gigs
          </Button>
          <Button
            onClick={() => navigate('/talent/settlements')}
            className="h-20 text-lg bg-gradient-to-br from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white font-semibold shadow-sm"
            data-testid="view-settlements-button"
          >
            <DollarSign className="w-5 h-5 mr-2" />
            My Settlements
          </Button>
          <Button
            onClick={() => navigate('/chat')}
            variant="outline"
            className="h-20 text-lg border-gray-300 text-gray-700 hover:bg-gray-100"
            data-testid="open-messages-button"
          >
            <MessageSquare className="w-5 h-5 mr-2" />
            Messages
          </Button>
          <Button
            onClick={() => navigate('/talent/quizzes')}
            variant="outline"
            className="h-20 text-lg border-gray-300 text-gray-700 hover:bg-gray-100"
            data-testid="assessments-button"
          >
            <ClipboardCheck className="w-5 h-5 mr-2" />
            Assessments
          </Button>
          <Button
            onClick={() => navigate('/talent/ratings')}
            variant="outline"
            className="h-20 text-lg border-gray-300 text-gray-700 hover:bg-gray-100"
            data-testid="rate-clients-button"
          >
            <Star className="w-5 h-5 mr-2" />
            Rate Gigs
          </Button>
          <Button
            onClick={() => navigate('/talent/profile-boost')}
            variant="outline"
            className="h-20 text-lg border-amber-300 text-amber-700 hover:bg-amber-50"
            data-testid="profile-boost-button"
          >
            <Crown className="w-5 h-5 mr-2" />
            Boost Profile
          </Button>
          <Button
             /* Payment consistency P0 (2026-08-30) — always visible so
                talents can view their eligibility page, but the label +
                copy no longer imply universal availability. Actual
                eligibility gating happens on `/talent/instant-payout`. */
            onClick={() => navigate('/talent/instant-payout')}
            variant="outline"
            className="h-20 text-lg border-cyan-300 text-cyan-700 hover:bg-cyan-50"
            data-testid="instant-payout-button"
          >
            <Zap className="w-5 h-5 mr-2" />
            Instant Payout
            <span className="sr-only"> — check per-Gig eligibility</span>
          </Button>
          <Button
            onClick={() => navigate('/talent/portfolio')}
            variant="outline"
            className="h-20 text-lg border-gray-300 text-gray-700 hover:bg-gray-100"
            data-testid="portfolio-button"
          >
            <Image className="w-5 h-5 mr-2" />
            Portfolio
          </Button>
          <Button
            onClick={() => navigate('/talent/gig-roles')}
            variant="outline"
            className="h-20 text-lg border-cyan-300 text-cyan-700 hover:bg-cyan-50"
            data-testid="gig-roles-button"
          >
            <Briefcase className="w-5 h-5 mr-2" />
            Gig Roles
          </Button>
          <Button
            onClick={() => navigate('/talent/certifications')}
            variant="outline"
            className="h-20 text-lg border-green-300 text-green-700 hover:bg-green-50"
            data-testid="certifications-button"
          >
            <Award className="w-5 h-5 mr-2" />
            Certifications
          </Button>
          <Button
            onClick={() => navigate('/talent/my-applications')}
            variant="outline"
            className="h-20 text-lg border-gray-300 text-gray-700 hover:bg-gray-100"
            data-testid="applications-button"
          >
            <Clipboard className="w-5 h-5 mr-2" />
            Applied Gigs
          </Button>
          <Button
            onClick={() => navigate('/talent/notification-settings')}
            variant="outline"
            className="h-20 text-lg border-gray-300 text-gray-700 hover:bg-gray-100"
            data-testid="notification-settings-button"
          >
            <Bell className="w-5 h-5 mr-2" />
            Notifications
          </Button>
        </div>

        {/* Growth tips — ongoing development guidance, moved off Home */}
        <div className="mt-6">
          <TalentGrowthTips compact={false} />
        </div>

        {/* Workers Comp upload modal — invoked from the platform-adjustment notice above. */}
        <WorkersCompUploadModal
          open={workersCompOpen}
          onOpenChange={setWorkersCompOpen}
        />
      </div>
    </div>
  );
};

export default TalentDashboard;
